import { Link, useLocation } from 'react-router-dom';
import { useSeo } from '../components/custom/Seo';

const PAGES = {
  '/about': {
    title: 'About DC Sports Report',
    body: [
      'DC Sports Report is an independent sports media outlet dedicated to covering Washington, D.C.-area teams — the Commanders, Nationals, Capitals, Wizards, Mystics, DC United, Washington Spirit, and DC Defenders.',
      'We provide news, analysis, live scoreboards, and conversation for D.C. sports fans, along with the DC Sports Report Podcast featuring in-depth discussion of the biggest stories around the District.',
      'DC Sports Report is a fan-founded, independent publication. We are not affiliated with, endorsed by, or connected to any of the teams we cover or their parent organizations.',
    ],
  },
  '/contact': {
    title: 'Contact Us',
    body: [
      'We want to hear from you — story tips, feedback, corrections, or podcast topic suggestions.',
      'The fastest way to reach the DC Sports Report team is through our social channels:',
    ],
    links: [
      { label: 'X / Twitter — @dcsports_report', url: 'https://x.com/dcsports_report' },
      { label: 'Instagram — @dc_sports_report', url: 'https://instagram.com/dc_sports_report' },
      { label: 'Facebook — DC Sports Report', url: 'https://www.facebook.com/profile.php?id=61572918715634' },
    ],
    footer: 'You can also subscribe to our newsletter on the home page to get headlines, recaps, and podcast drops delivered to your inbox.',
  },
  '/privacy': {
    title: 'Privacy Policy',
    body: [
      'DC Sports Report respects your privacy. This page explains what information we collect and how it is used.',
      'Newsletter: When you subscribe to our newsletter, we collect your name and email address. This information is used solely to deliver DC Sports Report content and is never sold to third parties.',
      'Analytics: We use standard web analytics (Google Analytics) to understand how visitors use the site — such as pages visited and time on site. This data is aggregated and does not personally identify you.',
      'Third-Party Content: Some pages embed content from third-party platforms (such as podcast players and social media). Those platforms may collect data according to their own privacy policies.',
      'Questions about this policy can be sent to us through any of our social channels listed on the Contact page.',
    ],
  },
  '/editorial-standards': {
    title: 'Editorial Standards',
    body: [
      'DC Sports Report is committed to accurate, fair, and independent coverage of Washington, D.C. sports.',
      'Independence: We are an independent publication. Our coverage is not influenced by any team, league, or sponsor.',
      'Accuracy: We strive to verify information before publishing. Statistics, scores, and transaction details are sourced from official league data and reputable reporting.',
      'Opinion vs. News: Analysis and opinion pieces reflect the views of their authors and are presented as such.',
      'Corrections: When we get something wrong, we fix it. See our Corrections page for how to report an error.',
    ],
  },
  '/corrections': {
    title: 'Corrections',
    body: [
      'DC Sports Report is committed to accuracy. When errors occur, we correct them promptly and transparently.',
      'If you believe we have published something inaccurate, please reach out through any of our social channels listed on the Contact page. Include the article headline and a description of the error.',
      'Corrected articles are updated as soon as the error is verified.',
    ],
  },
};

export default function StaticPage() {
  const location = useLocation();
  const page = PAGES[location.pathname];

  useSeo({
    title: page ? `${page.title} | DC Sports Report` : undefined,
    description: page ? page.body[0] : undefined,
    path: location.pathname,
  });

  if (!page) return null;

  return (
    <div data-testid={`static-page-${location.pathname.slice(1)}`} className="min-h-screen bg-white">
      <div className="bg-[#002868] border-b-2 border-[#BF0A30]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="font-serif-head font-extrabold text-3xl sm:text-4xl text-white tracking-tight">
            {page.title}
          </h1>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-5">
          {page.body.map((para) => (
            <p key={para.slice(0, 40)} className="text-slate-700 text-base leading-relaxed">{para}</p>
          ))}
          {page.links && (
            <ul className="space-y-2 pl-1">
              {page.links.map((l) => (
                <li key={l.url}>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#002868] font-semibold underline underline-offset-2 hover:text-[#BF0A30] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
          {page.footer && <p className="text-slate-700 text-base leading-relaxed">{page.footer}</p>}
        </div>
        <div className="mt-10 pt-6 border-t border-slate-200">
          <Link to="/" className="text-sm font-bold uppercase tracking-[0.15em] text-slate-500 hover:text-[#BF0A30] transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
